import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
    INGREDIENT_CATEGORIES,
    catColor,
    catLabel,
    euroPrecise,
} from "../lib/constants";

export function IngredientPicker({ open, onOpenChange, ingredients, onPick }) {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");

    useEffect(() => {
        if (!open) {
            setQ("");
            setCat("all");
        }
    }, [open]);

    // Calcola contatori per categoria sull'intero set per la barra filtri
    const counts = useMemo(() => {
        const map = { all: ingredients.length };
        for (const c of INGREDIENT_CATEGORIES) map[c] = 0;
        for (const i of ingredients) {
            if (map[i.category] != null) map[i.category] += 1;
        }
        return map;
    }, [ingredients]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return ingredients.filter((i) => {
            if (cat !== "all" && i.category !== cat) return false;
            if (!s) return true;
            return (
                i.name.toLowerCase().includes(s) ||
                catLabel(i.category).toLowerCase().includes(s)
            );
        });
    }, [q, cat, ingredients]);

    const chip = (key, label, color) => {
        const active = cat === key;
        const n = counts[key] ?? 0;
        return (
            <button
                key={key}
                type="button"
                data-testid={`picker-cat-${key}`}
                onClick={() => setCat(key)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-semibold transition ${
                    active
                        ? "bg-brand-navy text-white border-brand-navy"
                        : "bg-white text-brand-ink border-brand-line hover:border-brand-gold"
                }`}
            >
                {color && (
                    <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: color }}
                    />
                )}
                {label}
                <span
                    className={`text-[10px] font-bold ${
                        active ? "text-brand-gold" : "text-brand-muted"
                    }`}
                >
                    {n}
                </span>
            </button>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl sm:max-w-3xl p-0 overflow-hidden bg-white"
                data-testid="ingredient-picker"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-brand-line">
                    <DialogTitle className="font-display text-2xl text-brand-ink">
                        Scegli ingrediente
                    </DialogTitle>
                    <div className="relative mt-3">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
                        />
                        <input
                            data-testid="ingredient-picker-search"
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca ingrediente…"
                            className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-brand-cream border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                        {q && (
                            <button
                                onClick={() => setQ("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted"
                                aria-label="Pulisci ricerca"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <div
                        className="mt-3 flex flex-wrap gap-1.5"
                        data-testid="ingredient-picker-categories"
                    >
                        {chip("all", "Tutti")}
                        {INGREDIENT_CATEGORIES.map((c) =>
                            chip(c, catLabel(c), catColor(c))
                        )}
                    </div>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto">
                    {filtered.length === 0 ? (
                        <div className="p-10 text-center text-sm text-brand-muted">
                            Nessun ingrediente trovato
                            {cat !== "all" && (
                                <>
                                    {" "}
                                    nella categoria{" "}
                                    <strong>{catLabel(cat)}</strong>
                                </>
                            )}
                            .
                        </div>
                    ) : (
                        filtered.map((i) => (
                            <button
                                key={i.id}
                                data-testid={`picker-item-${i.id}`}
                                onClick={() => {
                                    onPick(i);
                                    onOpenChange(false);
                                }}
                                className="w-full flex items-center gap-3 px-6 py-3 hover:bg-brand-cream transition text-left"
                            >
                                <span
                                    className="h-8 w-1.5 rounded-full shrink-0"
                                    style={{ background: catColor(i.category) }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                        {i.name}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-wider text-brand-muted">
                                        {catLabel(i.category)} · scarto {i.waste_pct || 0}%
                                    </div>
                                </div>
                                <div className="text-sm font-semibold text-brand-ink">
                                    {euroPrecise(i.cost_per_unit)}
                                    <span className="ml-1 text-[11px] text-brand-muted font-normal">
                                        /{i.unit}
                                    </span>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
